/**
 * Mission Detail Page (Content Moderation)
 * Display detailed mission information and moderation actions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Warning as WarningIcon,
  PersonOff as PersonOffIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { ContentModerationService } from '../../../services/contentModeration/ContentModerationService';
import type { InvestigationDetail } from '../../../types/contentModeration';
import {
  Card,
  CardContent,
  TextField,
  PrimaryButton,
  SecondaryButton,
} from '../../../components/ui';
import { formatDateTime } from '../../../utils/dateUtils';

const InvestigationDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [mission, setMission] = useState<InvestigationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [internalNotes, setInternalNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  useEffect(() => {
    const fetchMissionDetail = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const detail = await ContentModerationService.getInvestigationDetail(id);
        setMission(detail);
        if (detail?.internalNotes) {
          setInternalNotes(detail.internalNotes);
        }
      } catch (error) {
        console.error('Failed to fetch mission detail:', error);
        setMission(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMissionDetail();
  }, [id]);

  const handleSaveNotes = async () => {
    if (!mission) return;
    try {
      setIsSavingNotes(true);
      await ContentModerationService.updateInternalNotes(mission.id, internalNotes);
      // Update local state
      setMission({ ...mission, internalNotes });
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleIssueWarning = () => {
    // TODO: Implement issue warning
    console.log('Issue warning');
  };

  const handleSuspendUser = () => {
    // TODO: Implement suspend user
    console.log('Suspend user');
  };

  const handleCancelMission = () => {
    // TODO: Implement cancel mission
    console.log('Cancel mission');
  };

  // Activity log helper removed as currently unused

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!mission) {
    return (
      <MainLayout>
        <EmptyState
          title="Mission Not Found"
          message="The mission you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ marginBottom: theme.spacing(2) }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/content-moderation')}
          sx={{
            color: theme.palette.text.secondary,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
            cursor: 'pointer',
          }}
        >
          Content Moderation
        </Link>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          Mission {mission.missionId}
        </Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(4),
        }}
      >
        Content Detail
      </Typography>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            {/* Flagged Content */}
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: '1rem',
                    }}
                  >
                    Flagged Content
                  </Typography>
                  {mission.missionId && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/missions/${mission.missionId}`)}
                      sx={{
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View Mission
                    </Link>
                  )}
                </Box>
                {mission.flaggedContentText ? (
                  <Box
                    sx={{
                      padding: theme.spacing(2.5),
                      backgroundColor: theme.customColors.searchBackground,
                      borderRadius: '4px',
                      border: `1px solid ${theme.customColors.tableBorder}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        lineHeight: 1.6,
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {mission.flaggedContentText}
                    </Typography>
                  </Box>
                ) : mission.flaggedContentImageUrl ? (
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      backgroundColor: theme.customColors.searchBackground,
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        filter: 'blur(8px)',
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    >
                      <img
                        src={mission.flaggedContentImageUrl}
                        alt="Flagged Content"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://via.placeholder.com/800x600/CCCCCC/666666?text=Flagged+Content`;
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      padding: theme.spacing(4),
                      textAlign: 'center',
                      backgroundColor: theme.customColors.searchBackground,
                      borderRadius: '4px',
                      border: `1px solid ${theme.customColors.tableBorder}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      No flagged content available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Metadata
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      MISSION ID:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {mission.missionId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      REASON FOR FLAG:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      AI Model ({mission.reasonForFlagging})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      UPLOAD DATE:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {formatDateTime(mission.uploadDate) || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      FLAG DATE:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {formatDateTime(mission.flagDate || mission.dateFlagged)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Activity Log - Hidden for now, will be added later */}
            {/* <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Activity Log
                </Typography>
                {mission.activityLog && mission.activityLog.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                    {mission.activityLog.map((activity) => (
                      <Box
                        key={activity.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: theme.spacing(2),
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: theme.customColors.searchBackground,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            flexShrink: 0,
                          }}
                        >
                          {getActivityIcon(activity.icon)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontSize: '0.875rem',
                              marginBottom: theme.spacing(0.5),
                            }}
                          >
                            {activity.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                            }}
                          >
                            {activity.timestampText}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    No activity log available
                  </Typography>
                )}
              </CardContent>
            </Card> */}
          </Box>
        </Grid>

        {/* Right Column - Moderation Actions */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            {/* Moderation Actions */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Moderation Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
                  <PrimaryButton
                    startIcon={<WarningIcon />}
                    onClick={handleIssueWarning}
                    sx={{
                      backgroundColor: '#FFC107',
                      color: '#000',
                      padding: theme.spacing(1.5),
                      '&:hover': {
                        backgroundColor: '#FFB300',
                      },
                    }}
                  >
                    Issue Warning
                  </PrimaryButton>
                  <PrimaryButton
                    startIcon={<PersonOffIcon />}
                    onClick={handleSuspendUser}
                    sx={{
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.error.contrastText,
                      padding: theme.spacing(1.5),
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                      },
                    }}
                  >
                    Suspend User
                  </PrimaryButton>
                  <SecondaryButton
                    startIcon={<CancelIcon />}
                    onClick={handleCancelMission}
                    sx={{
                      padding: theme.spacing(1.5),
                    }}
                  >
                    Cancel Mission
                  </SecondaryButton>
                </Box>
              </CardContent>
            </Card>

            {/* Internal Notes */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Internal Notes (Optional)
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  placeholder="Add notes about your decision..."
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  onBlur={handleSaveNotes}
                  disabled={isSavingNotes}
                  sx={{
                    width: '100%',
                  }}
                />
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default InvestigationDetailPage;

